<template>
	<SuiSelectListButton
		:aria-selected="isSelected"
		:aria-label="
			$t('user_assign_select_user_label', {
				name: user.name,
			})
		"
		:active="isSelected"
		:data-index="dataIndex"
		class="user-assign-select-list-user-item"
		@click="$emit('click', user.id)"
	>
		<UserAvatar :user :availability />
		<div
			v-tooltip="roleTooltip"
			:class="[
				'user-assign-select-list-user-item__info',
				{
					'user-assign-select-list-user-item__info--inactive':
						user.status === UserStatus.Inactive,
				},
			]"
		>
			<span class="user-assign-select-list-user-item__name">
				{{ user.name }}
			</span>
			<template v-if="shouldShowRoleInfo">
				<span class="user-assign-select-list-user-item__role-separator"
					>|</span
				>
				<span class="user-assign-select-list-user-item__role">{{
					user.role?.name
				}}</span>
			</template>
		</div>
		<SuiIcon
			v-if="isSelected"
			name="icon-tick-16"
			class="user-assign-select-list-user-item__tick"
		/>
	</SuiSelectListButton>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SuiSelectListButton, SuiIcon } from '@developers/scoro-ui';
import {
	type User,
	type UserAssignSelectUserFragment,
	UserStatus,
	type UserAssignSelectAvailabilityQuery,
} from '@/generated/graphql';
import UserAvatar from '../../UserAvatar/UserAvatar.vue';
import { getUserAvailability, getUserRoleTooltip } from '../../../utils';
import { type UserAssignGroup, UserAssignGroups } from '../../../types';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { Setting } from '@/constants/settings';

export interface UserAssignSelectListUserItemProps {
	user: UserAssignSelectUserFragment;
	isSelected: boolean;
	taskUsersAvailability: UserAssignSelectAvailabilityQuery['TaskUsersAvailability'];
	groupId: UserAssignGroup['id'];
	dataIndex?: number;
}

const props = defineProps<UserAssignSelectListUserItemProps>();

defineEmits<{
	click: [userId: User['id']];
}>();

const [assignRolesToTasksSetting] = useBooleanSetting(
	Setting.ASSIGN_ROLES_TO_TASKS
);

const availability = computed(() =>
	getUserAvailability(props.user.id, props.taskUsersAvailability)
);

const roleTooltip = computed(() =>
	getUserRoleTooltip(props.groupId, props.user)
);

const shouldShowRoleInfo = computed(
	() =>
		assignRolesToTasksSetting.value &&
		props.groupId === UserAssignGroups.ProjectMembers &&
		!!props.user.role
);
</script>

<style scoped lang="scss">
.user-assign-select-list-user-item {
	user-select: none;

	&__info {
		display: flex;
		align-items: baseline;
		gap: 0.5rem;
		min-width: 0;

		&--inactive {
			color: $neutral-60;
		}
	}

	&__name {
		white-space: nowrap;
	}

	&__role-separator,
	&__role {
		font-size: 1.5rem;
		color: $neutral-70;
	}

	&__role {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__tick {
		--sui-icon-fill: #{$blue-50};

		margin-left: auto;
	}
}
</style>
